import React, { useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  Checkbox,
  Button,
  Spinner,
  Text,
  Divider,
} from "@fluentui/react-components";
import { Tag24Regular } from "@fluentui/react-icons";
import type { TagSuggestion } from "../suggestions";
import type { MasterCategory } from "../mailbox-adapter";

interface TagSuggestButtonProps {
  disabled: boolean;
  disabledReason?: string;
  className: string;
  iconClassName: string;
  labelClassName: string;
  /** Fetches fresh AI suggestions + the full existing tag list. Called every
   * time the popover opens (never cached) so a different open email always
   * gets its own suggestions. */
  onSuggest: () => Promise<{ suggestions: TagSuggestion[]; existing: MasterCategory[] }>;
  /** Called once with the full checked set when "Apply" is clicked. */
  onApply: (tagNames: string[]) => Promise<void>;
}

export function TagSuggestButton({
  disabled,
  disabledReason,
  className,
  iconClassName,
  labelClassName,
  onSuggest,
  onApply,
}: TagSuggestButtonProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [existing, setExisting] = useState<MasterCategory[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  async function handleOpenChange(_event: unknown, data: { open: boolean }) {
    setOpen(data.open);
    setSelected(new Set());
    if (!data.open) {
      return;
    }
    setLoading(true);
    setSuggestions([]);
    setExisting([]);
    // onSuggest is expected to catch its own errors and resolve with an empty
    // result — this catch/finally is a last-resort guard so a stray rejection
    // can never leave the popover spinning forever or surface as an
    // unhandled promise rejection (defense in depth, not the primary fix).
    try {
      const result = await onSuggest();
      setSuggestions(result.suggestions);
      setExisting(result.existing);
    } catch {
      setSuggestions([]);
      setExisting([]);
    } finally {
      setLoading(false);
    }
  }

  function setChecked(name: string, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(name);
      } else {
        next.delete(name);
      }
      return next;
    });
  }

  async function handleApply() {
    if (selected.size === 0) {
      return;
    }
    setApplying(true);
    try {
      await onApply(Array.from(selected));
      setOpen(false);
      setSelected(new Set());
    } catch {
      // onApply (ChatPanel's handleApplyTags) already catches its own errors
      // and shows a toast — this only guards against an unhandled rejection
      // if a future caller doesn't.
    } finally {
      setApplying(false);
    }
  }

  const suggestedNames = new Set(suggestions.map((s) => s.name.toLowerCase()));
  const otherTags = existing.filter((t) => !suggestedNames.has(t.displayName.toLowerCase()));

  return (
    <Popover open={open} onOpenChange={(e, data) => void handleOpenChange(e, data)}>
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={className}
          disabled={disabled}
          title={disabled ? disabledReason : "Suggest Tags"}
        >
          <Tag24Regular className={iconClassName} aria-hidden />
          <span className={labelClassName}>Suggest Tags</span>
        </button>
      </PopoverTrigger>
      <PopoverSurface>
        <div style={{ minWidth: 220, display: "flex", flexDirection: "column", gap: 6 }}>
          {loading ? (
            <Spinner size="tiny" label="Thinking…" />
          ) : suggestions.length === 0 && otherTags.length === 0 ? (
            <Text size={200}>No tags found</Text>
          ) : (
            <>
              {suggestions.length > 0 && (
                <>
                  <Text size={100} weight="semibold">
                    Suggested
                  </Text>
                  {suggestions.map((s) => (
                    <Checkbox
                      key={s.name}
                      label={s.name}
                      checked={selected.has(s.name)}
                      onChange={(_e, data) => setChecked(s.name, !!data.checked)}
                    />
                  ))}
                </>
              )}
              {otherTags.length > 0 && (
                <>
                  {suggestions.length > 0 && <Divider />}
                  <Text size={100} weight="semibold">
                    All tags
                  </Text>
                  {otherTags.map((t) => (
                    <Checkbox
                      key={t.id}
                      label={t.displayName}
                      checked={selected.has(t.displayName)}
                      onChange={(_e, data) => setChecked(t.displayName, !!data.checked)}
                    />
                  ))}
                </>
              )}
              <Button
                appearance="primary"
                size="small"
                disabled={selected.size === 0 || applying}
                onClick={() => void handleApply()}
              >
                {applying ? <Spinner size="tiny" /> : `Apply (${selected.size})`}
              </Button>
            </>
          )}
        </div>
      </PopoverSurface>
    </Popover>
  );
}
